import { useEffect, useMemo, useState } from 'react';
import { useIntl, getLocale } from 'umi';
import { ICardContentProp, TMetric, IResponseItem, ICardConfig, IMobileHooksProps } from '../type';
import { formatNum, formatPercentNum, isEmpty } from '@/pages/intelligence/utils/commonUtils';
import { getMobilePageData } from '@intelligence/services/marketProfile';
import ApnnieNochange from '@/pages/intelligence/assets/svg/dataSource/appnnie.svg';
import Apple from '@/pages/intelligence/assets/svg/dataSource/appstore.svg';
import Android from '@/pages/intelligence/assets/svg/dataSource/googleplay.svg';
// import Apple from '@/pages/intelligence/assets/svg/dataSource/apple.svg';
// import Android from '@/pages/intelligence/assets/svg/dataSource/android.svg';
import { METRICS, ALL_PLATFORMS, PLATFORMS } from '../constants';
import { calculGrowth } from '../_util';

interface IMobileCardConfigHooksProps extends IMobileHooksProps {
  fetchParams?: any;
}

export default function useCardConfig(props: IMobileCardConfigHooksProps) {
  const { dataSource, granularity, regions, genres, fetchParams } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const source = {
    [dataSource]: METRICS,
  };

  const [metricLoading, setMetricLoading] = useState<boolean>(true);
  const [platformLoading, setPlatformLoading] = useState<boolean>(true);
  const [metricConfig, setMetricConfig] = useState<ICardConfig[]>([]);
  const [platformConfig, setPlatformConfig] = useState<ICardConfig[]>([]);

  // const metricKeyMap: Record<TMetric, string> = useMemo(
  //   () => ({
  //     revenue: formatMessage({ id: 'dbi_revenue_$' }),
  //     download: formatMessage({ id: 'dbi_downloads' }),
  //     game_count: formatMessage({ id: 'dbi_market_total_game' }),
  //   }),
  //   [locale],
  // );

  // console.log('metricKeyMap:', { metricKeyMap, locale });

  /**
   * 将获取的数据项转换为卡片内容配置
   * @param item - 数据项，包含增长率、指标和值等信息
   * @returns 返回卡片内容配置对象，包含标签键、值、比较文本、提示文本和增长率
   */
  const getChildConfig = (item: IResponseItem): ICardContentProp => {
    // const { growth_ratio: growth, metric, value } = item;
    const { value_ratio: ratio, metric, value } = item;
    const growth = calculGrowth({ currNum: value, preNum: ratio });

    return {
      // label: metricKeyMap[metric],
      label: metric,
      value: value === null ? '-' : formatNum(value, true, 2),
      // compareText: 'MoM',
      compareText: '',
      tipHeadText: '',
      isUp: growth === null ? false : growth > 0,
      // growth: growth === null ? '-' : formatPercentNum(growth),
      growth: growth === null ? '-' : formatPercentNum(growth),
    };
  };

  /**
   * 生成市场概况卡片配置
   * @param data - 指标数据数组，默认为空数组
   * @returns 返回卡片配置数组，包含全球市场表现相关信息
   * @description 将原始指标数据转换为标准卡片配置格式，每个卡片包含标签、图标、提示信息和子项配置
   */
  const getMetricCardConfig = (data: IResponseItem[] = []): ICardConfig[] => [
    {
      // label: formatMessage({ id: 'dbi_market_global_perf' }),
      label: 'dbi_market_global_perf',
      // icon: ApnnieNochange,
      // msg: formatMessage({ id: 'dbi_market_global_tip' }),
      // msg: 'dbi_market_global_tip',
      children: data.map((metricItem: IResponseItem) => getChildConfig(metricItem)),
    },
  ];

  /**
   * 根据平台数据生成卡片配置
   * @param data - 平台响应数据数组，默认为空数组
   * @returns 返回包含 iOS 和 Android 平台卡片配置的数组
   * @description
   * - 将输入数据按平台(appstore/googleplay)分类
   * - 为每个平台创建对应的卡片配置
   * - iOS 卡片使用 Apple 图标
   * - Android 卡片使用 Android 图标
   */
  const getPlatformCardConfig = (data: IResponseItem[] = []): ICardConfig[] => {
    const appstoreList: ICardContentProp[] = [];
    const googleplayList: ICardContentProp[] = [];

    data.forEach((platformItem: IResponseItem) => {
      if (platformItem.platform === 'appstore') {
        appstoreList.push(getChildConfig(platformItem));
      } else if (platformItem.platform === 'googleplay') {
        googleplayList.push(getChildConfig(platformItem));
      }
    });

    Object.assign(appstoreList[1] || {}, {
      isBorderNone: true,
    });
    Object.assign(googleplayList[1] || {}, {
      isBorderNone: true,
    });

    return [
      {
        // label: `${formatMessage({ id: 'dbi_all' })} iOS`,
        label: 'dbi_all,iOS',
        icon: Apple,
        children: appstoreList,
      },
      {
        // label: `${formatMessage({ id: 'dbi_all' })} Android`,
        // label: 'dbi_all,Android',
        label: 'dbi_all,Google Play',
        icon: Android,
        children: googleplayList,
      },
    ];
  };

  const getGlobalPerformanceInfo = (params?: any) => {
    return (
      getMobilePageData({
        source_metrics: source,
        granularity,
        regions,
        genres,
        platforms: ALL_PLATFORMS,
        metrics: METRICS,
        b_growth_ratio: true,
        ...params,
      })
        // .then((cardMetricsData: any) => {
        .then((res: any) => {
          const cardMetricsData = res?.data || {};

          setMetricLoading(false);
          // setMetricConfig(getMetricCardConfig(cardMetricsData?.metric_value_list || []));
          const result = getMetricCardConfig(cardMetricsData?.metric_value_list || []);

          return result;
        })
        .catch(() => {
          setMetricLoading(false);
          return [];
        })
    );
  };

  const getPlatformInfo = (params?: any) => {
    return (
      getMobilePageData({
        source_metrics: source,
        granularity,
        regions,
        genres,
        platforms: PLATFORMS,
        metrics: METRICS,
        b_growth_ratio: true,
        b_group_by_platform: true,
        ...params,
      })
        // .then((cardPlatformData: any) => {
        .then((res: any) => {
          const cardPlatformData = res?.data || {};

          setPlatformLoading(false);
          // setPlatformConfig(getPlatformCardConfig(cardPlatformData?.metric_value_list || []));

          const result = getPlatformCardConfig(cardPlatformData?.metric_value_list || []);

          return result;
        })
        .catch(() => {
          setPlatformLoading(false);
          return [];
        })
    );
  };

  /**
   * 初始化市场概况页面的卡片配置数据
   *
   * 该函数执行两个并行的数据请求：
   * 1. 获取整体指标数据，用于展示总体市场表现
   * 2. 获取平台细分数据，用于对比 App Store 和 Google Play 的表现
   *
   * 在数据加载过程中会显示加载状态，请求完成后更新对应的卡片配置
   *
   * @async
   * @function initial
   */
  const initial = async (params?: any) => {
    setMetricLoading(true);
    setPlatformLoading(true);

    const restParams = {
      start_date: params?.start_date,
      end_date: params?.end_date,
    };

    const mConfig = await getGlobalPerformanceInfo(restParams);
    setMetricConfig(mConfig);

    const pConfig = await getPlatformInfo(restParams);
    setPlatformConfig(pConfig);
    // console.debug('pConfig', { pConfig });
  };

  useEffect(() => {
    if (fetchParams?.start_date && fetchParams?.end_date) {
      initial(fetchParams);
    }
  }, [dataSource, regions, genres, granularity, fetchParams.start_date, fetchParams.end_date]);

  return {
    metricConfig,
    platformConfig,
    metricLoading,
    platformLoading,
  };
}
